import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { ConfigContext } from '../../../contexts/config-context';
import { profilePhoto } from '../../../contexts/userContext';
import { AgencyIcon, CardPublisher, CardSpecs, LikeButton, OwnerIcon } from '../../main-page/components/featured-listings';
import { formatNumber, PRICE_UNITS } from '../../main-page/home-utils';
import { PhoneIcon } from '../../../components/shared-components/listing-icons';

const NEW_LISTING_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_FEATURES = 5;
const PHOTO_CYCLE_MS = 1600;
// share of the photo width a swipe has to cover to move to the next photo
const SWIPE_SHARE = 0.2;
// boolean listing fields worth showing on a card, most relevant first
const FEATURE_KEYS = ['registered', 'habitable', 'terrace', 'balcony', 'lift', 'parking', 'garage', 'ac', 'internet', 'basement',
    'water', 'electricity', 'wifi', 'pool', 'yard'];

export const listingPhoto = (listing, api, index) => listing.photos?.[index]
    ? `${api.host}:${api.port}${listing.photos[index].photo.ratios.null.sources['image/webp']['500']}`
    : `${api.host}:${api.port}/media/No_Image_Available.jpg`;

export const listingCover = (listing, api) => listingPhoto(listing, api, 0);

export const ResultCard = ({ listing, active, onHover, onMessage }) => {
    const { api } = useContext(ConfigContext);
    const { t, i18n } = useTranslation(["results", "home", "listing"]);
    const navigate = useNavigate();
    const [phoneShown, setPhoneShown] = useState(false);
    const [photoHover, setPhotoHover] = useState(false);
    const [photo, setPhoto] = useState(0);
    // slides are mounted on the first hover, so cards never hovered load only the cover
    const [slidesReady, setSlidesReady] = useState(false);
    // on touch screens the photos are swiped instead of cycled on hover
    const [swipe, setSwipe] = useState(false);
    const [dragX, setDragX] = useState(null);
    const touch = useRef(null);
    const swiped = useRef(false);

    const { id, photos, title, place, subplace, location, listing_type, user, date_created } = listing;
    const total = photos?.length || 0;

    // cycle through the listing photos while the pointer is over the photo
    useEffect(() => {
        if (!photoHover || swipe || total < 2) return;
        const timer = setInterval(() => setPhoto(p => (p + 1) % total), PHOTO_CYCLE_MS);
        return () => {
            clearInterval(timer);
            setPhoto(0);
        };
    }, [photoHover, swipe, total]);

    const onTouchStart = (e) => {
        swiped.current = false;
        if (total < 2) return;
        touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, axis: null };
        setSlidesReady(true);
        setSwipe(true);
    };

    const onTouchMove = (e) => {
        const start = touch.current;
        if (!start) return;
        const dx = e.touches[0].clientX - start.x;
        const dy = e.touches[0].clientY - start.y;
        // the first clear movement decides between swiping the photos and scrolling the page
        if (!start.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 8) start.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (start.axis !== 'x') return;
        const atEdge = (photo === 0 && dx > 0) || (photo === total - 1 && dx < 0);
        setDragX(atEdge ? dx / 3 : dx);
    };

    const onTouchEnd = (e) => {
        const start = touch.current;
        touch.current = null;
        if (!start || start.axis !== 'x') {
            setDragX(null);
            return;
        }
        // a swipe must not also open the listing
        swiped.current = true;
        const dx = e.changedTouches[0].clientX - start.x;
        const limit = e.currentTarget.offsetWidth * SWIPE_SHARE;
        if (dx < -limit) setPhoto(p => Math.min(p + 1, total - 1));
        if (dx > limit) setPhoto(p => Math.max(p - 1, 0));
        setDragX(null);
    };

    const openListing = () => {
        if (swiped.current) {
            swiped.current = false;
            return;
        }
        navigate(`/oglas/${id}`);
    };

    const price = parseFloat(listing.price) || 0;
    const area = parseFloat(listing.area) || 0;
    const typeIndex = (listing_type?.id || 1) - 1;
    const priceSuffix = typeIndex > 0 ? ` ${t(PRICE_UNITS[typeIndex], { ns: 'home' })}` : '';
    const days = Math.floor((Date.now() - new Date(date_created).getTime()) / DAY_MS);
    const publisher = user?.user_type?.label;
    const isAgency = /agenc/i.test(publisher || '');
    const avatar = profilePhoto(user?.photo);
    const features = FEATURE_KEYS.filter(key => listing.features?.includes(key)).slice(0, MAX_FEATURES).map(key => t(key, { ns: 'listing' }));
    const phone = listing.phone || listing.mobile;
    const published = days <= 0 ? t('published_today')
        : days === 1 ? t('published_yesterday')
            : days <= 30 ? t('published_days', { n: days })
                : t('published_on', { date: new Date(date_created).toLocaleDateString(i18n.language === 'sr' ? 'sr-RS' : 'en-GB') });

    const stop = (handler) => (e) => {
        e.stopPropagation();
        handler();
    };

    return (
        <article className={`hs-res-card ${active ? 'active' : ''}`} onClick={openListing}
            onMouseEnter={() => onHover(id)} onMouseLeave={() => onHover(null)}>
            <div className={`hs-res-photo ${swipe ? 'swipe' : ''} ${dragX !== null ? 'dragging' : ''}`} style={{ backgroundImage: `url(${listingCover(listing, api)})` }}
                onMouseEnter={() => { setSlidesReady(true); setPhotoHover(true); }} onMouseLeave={() => setPhotoHover(false)}
                onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onTouchCancel={onTouchEnd}>
                {slidesReady && total > 1 && photos.map((p, i) => (
                    <div key={`slide-${i}`} className={`hs-res-slide ${i === photo ? 'on' : ''}`}
                        style={{
                            backgroundImage: `url(${listingPhoto(listing, api, i)})`,
                            ...(swipe && { transform: `translateX(calc(${(i - photo) * 100}% + ${dragX || 0}px))` })
                        }}></div>
                ))}
                <div className='hs-res-badges'>
                    {listing.promoted && <span className='premium'>{listing.promoted_value?.label || 'Premium'}</span>}
                    {days < NEW_LISTING_DAYS && <span className='new'>{t('badge_new', { ns: 'home' })}</span>}
                </div>
                <span className='hs-res-photo-count'>{(photoHover || swipe) && total > 1 ? `${photo + 1}/${total}` : `${total} ${t('photos', { ns: 'home' })}`}</span>
            </div>
            <div className='hs-res-body'>
                <div className='hs-res-top'>
                    <div className='hs-res-price-row'>
                        <span className='hs-res-price'>{price > 0 ? `${formatNumber(price)} €${priceSuffix}` : t('price_placeholder', { ns: 'listing' })}</span>
                        {typeIndex === 0 && price > 0 && area > 0 && <span className='hs-res-ppm'>{formatNumber(price / area)} €/m²</span>}
                    </div>
                    <LikeButton listing={id} followers={listing.followers} className='hs-res-like' />
                </div>
                <h3 className='hs-res-title'>{title}</h3>
                <p className='hs-res-loc'>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21Z" /><circle cx="12" cy="9.5" r="2.5" /></svg>
                    {[place?.label, subplace?.label, location?.label].filter(Boolean).join(' · ')}
                </p>
                <CardSpecs listing={listing} />
                {features.length > 0 && <p className='hs-res-feats'>{features.join(' · ')}</p>}
                {/* phones end with the publisher like the home page card, instead of the footer with the buttons */}
                <CardPublisher user={user} />
                <div className='hs-res-footer'>
                    <span className={`hs-res-publisher-icon ${isAgency ? 'agency' : ''}`} style={avatar ? { backgroundImage: `url(${api.host}:${api.port}${avatar})` } : undefined}>
                        {!avatar && (isAgency ? <AgencyIcon /> : <OwnerIcon />)}
                    </span>
                    <span className='hs-res-publisher'>
                        <span className='hs-res-publisher-name'>{[user?.first_name, user?.last_name].filter(Boolean).join(' ') || publisher}</span>
                        <span className='hs-res-publisher-date'>{published}</span>
                    </span>
                    <span className='hs-spacer'></span>
                    <button className='hs-res-msg' onClick={stop(() => onMessage(listing))}>{t('message')}</button>
                    {phone && (phoneShown
                        ? <a className='hs-res-call' href={`tel:${phone}`} onClick={(e) => e.stopPropagation()}><PhoneIcon size={14} />{phone}</a>
                        : <button className='hs-res-call' onClick={stop(() => setPhoneShown(true))}><PhoneIcon size={14} />{t('call')}</button>)}
                </div>
            </div>
        </article>
    );
};
